/**
 * useAcquisitionMetadata
 *
 * Collects client-side acquisition signals for attribution when a participant
 * enrolls. Reused anywhere we want to capture how a visitor arrived
 * (anonymous "Join now", express verify, etc.).
 *
 * Signals collected:
 *   - referrer  → document.referrer
 *   - UTM       → window.location.search (utm_source/medium/campaign)
 *   - userAgent → navigator.userAgent
 *   - language  → navigator.language
 *   - timezone  → Intl.DateTimeFormat().resolvedOptions().timeZone
 *
 * All reads are defensive (SSR/Storybook-safe) and only present keys are
 * included so the resulting metadata stays compact.
 */

import type { EnrollMetadata } from '@/services/graphql/nexus/accessPolicy';

export function useAcquisitionMetadata() {
  /**
   * Build a one-shot snapshot of the current acquisition signals.
   */
  const collect = (): EnrollMetadata => {
    const metadata: EnrollMetadata = {};

    try {
      if (typeof document !== 'undefined' && document.referrer) {
        metadata.referrer = document.referrer;
      }
    } catch {
      /* no-op */
    }

    try {
      if (typeof window !== 'undefined' && window.location?.search) {
        const params = new URLSearchParams(window.location.search);
        const utmSource = params.get('utm_source');
        const utmMedium = params.get('utm_medium');
        const utmCampaign = params.get('utm_campaign');
        if (utmSource) metadata.utmSource = utmSource;
        if (utmMedium) metadata.utmMedium = utmMedium;
        if (utmCampaign) metadata.utmCampaign = utmCampaign;
      }
    } catch {
      /* no-op */
    }

    try {
      if (typeof navigator !== 'undefined') {
        if (navigator.userAgent) metadata.userAgent = navigator.userAgent;
        if (navigator.language) metadata.language = navigator.language;
      }
    } catch {
      /* no-op */
    }

    try {
      const tz = Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone;
      if (tz) metadata.timezone = tz;
    } catch {
      /* no-op */
    }

    return metadata;
  };

  return { collect };
}
